import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tag } from '@agconnections/grow-ui';
import { TileTag } from '../helpers/tileTags';

const BADGE_MIN_WIDTH = 44;
const MIN_TRUNCATABLE_PX = 20;
const TAG_MARGIN_PX = 8;

interface LayoutResult {
  visibleCount: number;
  truncatableIdx: number;
  truncatableMaxWidth: number;
  hiddenCount: number;
}

export function computeTagLayout(
  naturalWidths: number[],
  containerWidth: number,
  tagCount: number
): LayoutResult {
  const showAll: LayoutResult = {
    visibleCount: tagCount,
    truncatableIdx: -1,
    truncatableMaxWidth: 0,
    hiddenCount: 0
  };

  if (!tagCount || !containerWidth) return showAll;

  const total = naturalWidths.slice(0, tagCount).reduce((s, w) => s + w, 0);
  if (total <= containerWidth) return showAll;

  // Some tags overflow. Walk forward fitting full tags while reserving badge space.
  let usedWidth = 0;
  let fullCount = 0;

  for (let i = 0; i < tagCount; i += 1) {
    const w = naturalWidths[i] ?? 0;
    if (usedWidth + w + BADGE_MIN_WIDTH <= containerWidth) {
      usedWidth += w;
      fullCount = i + 1;
    } else {
      break;
    }
  }

  const nextIdx = fullCount;
  const remaining = tagCount - nextIdx;

  if (remaining <= 0) return showAll;

  if (remaining === 1) {
    // Last tag: truncate in-place without a badge.
    const available = containerWidth - usedWidth - TAG_MARGIN_PX;
    if (available >= MIN_TRUNCATABLE_PX) {
      return {
        visibleCount: nextIdx,
        truncatableIdx: nextIdx,
        truncatableMaxWidth: available,
        hiddenCount: 0
      };
    }
    return {
      visibleCount: nextIdx,
      truncatableIdx: -1,
      truncatableMaxWidth: 0,
      hiddenCount: 1
    };
  }

  // Multiple tags remain: truncate one + show badge for the rest.
  const available =
    containerWidth - usedWidth - BADGE_MIN_WIDTH - TAG_MARGIN_PX;
  if (available >= MIN_TRUNCATABLE_PX) {
    return {
      visibleCount: nextIdx,
      truncatableIdx: nextIdx,
      truncatableMaxWidth: available,
      hiddenCount: remaining - 1
    };
  }
  return {
    visibleCount: nextIdx,
    truncatableIdx: -1,
    truncatableMaxWidth: 0,
    hiddenCount: remaining
  };
}

// Callers rebuild their tag array every render, so the layout effects below
// re-run constantly; keep the same layout object when nothing moved so each
// row doesn't pay for a second render pass.
function applyLayoutIfChanged(
  setLayout: React.Dispatch<React.SetStateAction<LayoutResult | null>>,
  next: LayoutResult
) {
  setLayout(prev =>
    prev?.visibleCount === next.visibleCount &&
    prev?.truncatableIdx === next.truncatableIdx &&
    prev?.truncatableMaxWidth === next.truncatableMaxWidth &&
    prev?.hiddenCount === next.hiddenCount
      ? prev
      : next
  );
}

export interface TileTagListProps {
  tags: TileTag[];
  /** Extra classes for the wrapper, e.g. the vertical margins a tile needs. */
  className?: string;
}

const TileTagList: React.FC<TileTagListProps> = ({ tags, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureTagRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cachedWidths = useRef<number[]>([]);
  const [layout, setLayout] = useState<LayoutResult | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const widths = measureTagRefs.current
      .slice(0, tags.length)
      .map(el => (el ? el.offsetWidth + TAG_MARGIN_PX : 0));

    cachedWidths.current = widths;
    applyLayoutIfChanged(
      setLayout,
      computeTagLayout(widths, containerWidth, tags.length)
    );
  }, [tags]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return () => {};

    const ro = new ResizeObserver(() => {
      const containerWidth = container.clientWidth;
      const widths = cachedWidths.current;
      applyLayoutIfChanged(
        setLayout,
        computeTagLayout(widths, containerWidth, widths.length)
      );
    });

    ro.observe(container);
    return () => ro.disconnect();
    // Nothing is rendered while there are no tags, so the ref is still null on
    // that first pass; re-run once tags arrive or the observer is never attached.
  }, [tags.length]);

  if (!tags.length) return null;

  const {
    visibleCount,
    truncatableIdx,
    truncatableMaxWidth,
    hiddenCount
  } = layout ?? {
    visibleCount: tags.length,
    truncatableIdx: -1,
    truncatableMaxWidth: 0,
    hiddenCount: 0
  };

  return (
    <div
      ref={containerRef}
      className={`relative flex w-full min-w-0 -ml-1 overflow-hidden **:data-[testid=gr-tag]:min-w-0 **:data-[testid=gr-tag]:max-w-full **:data-[testid=gr-tag]:overflow-hidden ${className}`}
      style={{ contain: 'inline-size' }}
      data-testid="tile-tag-list"
    >
      {/* Hidden measurement layer — always renders all tags at natural size */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          flexWrap: 'nowrap',
          top: 0,
          left: 0
        }}
      >
        {tags.map((tag, i) => (
          <div
            key={tag.id}
            ref={el => {
              measureTagRefs.current[i] = el;
            }}
            style={{ flexShrink: 0 }}
            className="m-1"
          >
            <Tag id={tag.id} color={tag.color}>
              {tag.name}
            </Tag>
          </div>
        ))}
      </div>

      {/* Fully visible tags */}
      {tags.slice(0, visibleCount).map(tag => (
        <div
          key={tag.id}
          className="m-1 min-w-0 overflow-hidden shrink-0"
          style={{ maxWidth: 'fit-content' }}
        >
          <Tag id={tag.id} color={tag.color}>
            <span className="block w-full min-w-0 truncate" title={tag.name}>
              {tag.name}
            </span>
          </Tag>
        </div>
      ))}

      {/* Last truncatable tag */}
      {truncatableIdx >= 0 && tags[truncatableIdx] != null && (
        <div
          className="m-1 min-w-0 overflow-hidden shrink-0"
          style={{ maxWidth: `${truncatableMaxWidth}px` }}
        >
          <Tag id={tags[truncatableIdx].id} color={tags[truncatableIdx].color}>
            <span
              className="block w-full min-w-0 truncate"
              title={tags[truncatableIdx].name}
            >
              {tags[truncatableIdx].name}
            </span>
          </Tag>
        </div>
      )}

      {/* Overflow badge — only shown when fully hidden tags exist */}
      {hiddenCount > 0 && (
        <div className="m-1 min-w-0 overflow-hidden shrink-0">
          <Tag id="overflow-badge">{`+${hiddenCount}`}</Tag>
        </div>
      )}
    </div>
  );
};

export default TileTagList;
